import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, Plus, Trash2, Send, Bot, User, Loader2 } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import {
	useConversationList,
	useConversation,
	useCreateConversation,
	useSendMessage,
	useDeleteConversation,
} from "../hooks/useConversations";
import { usePublicSettings } from "../hooks/usePublicSettings";
import styles from "./Chat.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/chat",
	component: withPermission(ChatPage, "ai", "read"),
});

function ChatPage() {
	const { t } = useTranslation();
	const { aiBotName } = usePublicSettings();
	const [activeId, setActiveId] = useState<string | null>(null);
	const [input, setInput] = useState("");
	const [pendingMsg, setPendingMsg] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const { data: convList, isLoading: listLoading, error: listError } = useConversationList();
	const {
		data: convData,
		isLoading: convLoading,
		refetch: refetchConv,
	} = useConversation(activeId ?? undefined);
	const createConv = useCreateConversation();
	const sendMsg = useSendMessage();
	const deleteConv = useDeleteConversation();

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [convData?.messages]);

	// Poll for assistant response when pending
	useEffect(() => {
		if (!pendingMsg || !activeId) return;

		const msgCount = convData?.messages.length ?? 0;
		let attempts = 0;
		const maxAttempts = 60; // 60 * 2s = 120s max

		pollRef.current = setInterval(async () => {
			attempts++;
			const result = await refetchConv();
			const newMsgCount = result.data?.messages.length ?? 0;

			// Stop if we got a new assistant message or if max attempts reached
			if (newMsgCount > msgCount) {
				const lastMsg = result.data?.messages[result.data.messages.length - 1];
				if (lastMsg?.role === "assistant") {
					setPendingMsg(false);
					if (lastMsg.isError) {
						setErrorMsg(lastMsg.content);
					} else {
						setErrorMsg(null);
					}
				}
			}

			if (attempts >= maxAttempts) {
				setPendingMsg(false);
				setErrorMsg(t("aiTimeout"));
			}
		}, 2000);

		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current);
				pollRef.current = null;
			}
		};
	}, [pendingMsg, activeId, convData?.messages.length, refetchConv, t]);

	const handleNewChat = async () => {
		const conv = await createConv.mutateAsync({ title: t("newChat"), mode: "chat" });
		setActiveId(conv.id);
		setErrorMsg(null);
	};

	const handleSend = async () => {
		if (!input.trim() || !activeId) return;
		const content = input.trim();
		setInput("");
		setErrorMsg(null);
		setPendingMsg(true);
		try {
			await sendMsg.mutateAsync({ id: activeId, content });
		} catch (err) {
			setPendingMsg(false);
			setErrorMsg(err instanceof Error ? err.message : t("error"));
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleDelete = async (id: string) => {
		await deleteConv.mutateAsync(id);
		if (activeId === id) {
			setActiveId(null);
			setPendingMsg(false);
		}
	};

	return (
		<div className={styles.container}>
			<aside className={styles.sidebar}>
				<button type="button" className={styles.newChatButton} onClick={handleNewChat}>
					<Plus size={16} />
					{t("newChat")}
				</button>

				{listLoading && <LoadingSpinner size="sm" />}
				{listError && <ErrorFallback message={listError.message} />}

				<div className={styles.conversationList}>
					{convList?.items.map((conv) => (
						<div
							key={conv.id}
							className={`${styles.conversationItem} ${activeId === conv.id ? styles.active : ""}`}
							onClick={() => {
								setActiveId(conv.id);
								setErrorMsg(null);
							}}
						>
							<MessageSquare size={14} />
							<span className={styles.convTitle}>{conv.title}</span>
							<button
								type="button"
								className={styles.deleteBtn}
								onClick={(e) => {
									e.stopPropagation();
									handleDelete(conv.id);
								}}
							>
								<Trash2 size={12} />
							</button>
						</div>
					))}
					{!convList?.items.length && !listLoading && (
						<p className={styles.empty}>{t("noConversations")}</p>
					)}
				</div>
			</aside>

			<main className={styles.main}>
				<PageHeader title={t("aiChat", { botName: aiBotName })} />

				{!activeId && (
					<div className={styles.welcome}>
						<Bot size={48} className={styles.welcomeIcon} />
						<h2>{t("aiWelcomeTitle", { botName: aiBotName })}</h2>
						<p>{t("aiWelcomeDesc")}</p>
						<button type="button" className={styles.startButton} onClick={handleNewChat}>
							{t("startChat")}
						</button>
					</div>
				)}

				{activeId && (
					<>
						<div className={styles.messages}>
							{convLoading && <LoadingSpinner />}
							{convData?.messages.map((msg) => (
								<div
									key={msg.id}
									className={`${styles.message} ${msg.role === "user" ? styles.userMessage : styles.assistantMessage}`}
								>
									<div className={styles.messageAvatar}>
										{msg.role === "user" ? <User size={16} /> : <Bot size={16} />}
									</div>
									<div className={styles.messageContent}>
										<div className={styles.messageText}>{msg.content}</div>
										{msg.toolCalls && (
											<div className={styles.toolCalls}>
												<pre>{JSON.stringify(msg.toolCalls, null, 2)}</pre>
											</div>
										)}
										{msg.toolResults && (
											<div className={styles.toolResults}>
												<pre>{JSON.stringify(msg.toolResults, null, 2)}</pre>
											</div>
										)}
									</div>
								</div>
							))}
							{pendingMsg && (
								<div className={`${styles.message} ${styles.assistantMessage}`}>
									<div className={styles.messageAvatar}>
										<Bot size={16} />
									</div>
									<div className={styles.messageContent}>
										<Loader2 size={16} className={styles.spin} />
										<span>{t("thinking")}</span>
									</div>
								</div>
							)}
							<div ref={messagesEndRef} />
						</div>

						{errorMsg && <div className={styles.errorBanner}>{errorMsg}</div>}

						<div className={styles.inputArea}>
							<textarea
								className={styles.input}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder={t("typeMessage")}
								rows={2}
							/>
							<button
								type="button"
								className={styles.sendButton}
								disabled={!input.trim() || sendMsg.isPending}
								onClick={handleSend}
							>
								<Send size={18} />
							</button>
						</div>
					</>
				)}
			</main>
		</div>
	);
}
